import { yupResolver } from '@hookform/resolvers/yup';
import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { UseBreakpoints } from '@my2n/use-breakpoints';
import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useCreateUserRfidMutation } from '../../apiRtk/be4feApi';
import { PATHS } from '../../config/consts';
import { useGetLoadedParams } from '../../hooks/paramsHooks';
import { useSurvicateEvent } from '../../hooks/useSurvicateEvent';
import { ApplicationContext, IModalBase } from '../../modules/commonTypes';
import { TYPE_DESCRIPTOR } from '../../modules/notifications';
import { addSnackbarError, addSnackbarSuccess } from '../../modules/notifications/actions';
import { selectors as siteSelectors } from '../../modules/sites';
import { actionsTS } from '../../modules/user';
import { getUserRfidCard } from '../../modules/user/actions';
import { TUserActionContext } from '../../modules/user/actionsTS';
import { IUser } from '../../modules/user/store';
import { replaceURLParams } from '../../routes/helpers/pathParamsReplacer';
import {
  assignRfidCardValidationSchema,
  FormFieldNames,
  getDefaultValues,
} from '../../validations/assignRfidCardValidationSchema';
import ActionModal from '../_DesignSystemWrappers/Modal/ActionModal';
import Alert from '../Alert';
import CardReader from '../CardReader';
import Link from '../Link';
import UserInfo from '../UserInfo';
import { getCreateUserRfidErrorMessage } from './getCreateUserRfidErrorMessage';
import { removeLeadingZeros } from './helpers';
import { useRefreshData } from './hooks/useRefreshData';
import messages from './messages';
import { DesktopScanQR } from './QRCodeReader/DesktopScanQR';
import { MobileScanQR } from './QRCodeReader/MobileScanQR';
import { ScanQRCodeButton } from './QRCodeReader/ScanQRCodeButton';
import RadioGroupCardType from './RadioGroupCardType/RadioGroupCardType';

interface IUserRfidModalNew extends IModalBase {
  user: IUser;
  context: TUserActionContext;
  userDataGridData?: { filter: { fulltext: string }; order: string; page: number; rowsPerPage: number };
}

export interface IFormProps {
  card: string;
}

const DORMAKABA_CARD_READER_PROPS = {
  allowedChars: /[0-9A-Fa-f]/,
  cardIdentifierInputProps: {
    slotProps: {
      htmlInput: {
        maxLength: 24,
      },
    },
  },
};

const STANDARD_CARD_READER_PROPS = {
  allowedChars: /[0-9A-Fa-f:]/,
  cardIdentifierInputProps: {
    slotProps: {
      htmlInput: {
        maxLength: 32,
      },
    },
  },
};

function UserRfidModalNew({ context, onClose, open, user, userDataGridData }: IUserRfidModalNew) {
  const { formatMessage } = useIntl();
  const { companyId, siteId, userId } = useGetLoadedParams(ApplicationContext.Site);
  const dispatch = useDispatch();
  const assignedRef = useRef<string | undefined>(undefined);
  const { triggerSurveyEvent } = useSurvicateEvent('RFID_assigned_site_admin');
  const { downSm } = UseBreakpoints();
  const [showScanCode, setShowScanCode] = useState(false);
  const isMobileScanActive = downSm && showScanCode;
  const siteData = useSelector(siteSelectors.getSiteData);
  const isSiteDormakabaIntegrationEnabled = siteData?.integrations?.DORMAKABA?.enabled;
  const { refreshData } = useRefreshData(context, userDataGridData);
  const formMethods = useForm<IFormProps>({
    defaultValues: getDefaultValues(isSiteDormakabaIntegrationEnabled),
    mode: 'onChange',
    resolver: yupResolver<any>(assignRfidCardValidationSchema(companyId, siteId, formatMessage, assignedRef)),
  });
  const cardType = formMethods.watch(FormFieldNames.CardType as 'card');
  const cardValue = formMethods.watch(FormFieldNames.Card);
  const previousCardTypeRef = useRef(cardType);
  const isDormakabaCardType = cardType === FormFieldNames.DormakabaCard;
  const [createUserRfid, { isLoading: createUserRfidLoading }] = useCreateUserRfidMutation();
  const formBoxPaddingTop = isSiteDormakabaIntegrationEnabled ? 3 : 0;

  useEffect(() => {
    setShowScanCode(false);

    if (previousCardTypeRef.current !== cardType) {
      formMethods.setValue(FormFieldNames.Card, '', { shouldDirty: true, shouldValidate: false });
      formMethods.clearErrors(FormFieldNames.Card);
      assignedRef.current = undefined;
    }

    previousCardTypeRef.current = cardType;
  }, [cardType, formMethods]);

  async function onSave() {
    isSiteDormakabaIntegrationEnabled && isDormakabaCardType
      ? await createUserRfid({
          companyId,
          createUserRfidRequest: {
            rfidNumber: removeLeadingZeros(formMethods.getValues(FormFieldNames.Card).toLowerCase()),
          },
          siteId,
          userUuid: user.uuid,
        })
          .unwrap()
          .then(() => {
            dispatch(addSnackbarSuccess({ ...messages.userSnackbarRfidCardAssignedSuccessfully }));
            dispatch(getUserRfidCard(companyId, siteId, userId));
            refreshData();
          })
          .catch((err) => {
            dispatch(addSnackbarError({ ...getCreateUserRfidErrorMessage(err) }, TYPE_DESCRIPTOR));
          })
      : dispatch(
          actionsTS.createUserRfid(companyId, siteId, user.id, formMethods.getValues('card').toUpperCase(), context)
        );
    onClose();
    triggerSurveyEvent();
  }

  function onCloseScanCode() {
    setShowScanCode(false);
  }

  function onToggleScanCode() {
    setShowScanCode((prev) => !prev);
  }

  const onScanCodeSuccess = (card: string) => {
    setShowScanCode(false);
    // Delay setting the value to allow CardReader to finish its initial test/clear cycle
    setTimeout(() => {
      formMethods.setValue('card', card, { shouldValidate: true });
    }, 500);
  };

  return (
    <ActionModal
      icon={<CheckIcon />}
      isPrimaryButtonDisabled={!cardValue || !formMethods.formState.isValid || createUserRfidLoading}
      muiDialogProps={
        isMobileScanActive ? { fullScreen: true } : { maxWidth: isSiteDormakabaIntegrationEnabled ? 'sm' : 'xs' }
      }
      onClose={onClose}
      open={open}
      primaryButtonAction={onSave}
      primaryButtonText={formatMessage(messages.userRfidModalConfirmButton)}
      secondaryButtonText={formatMessage(messages.userRfidModalCancelButton)}
      title={formatMessage(messages.userRfidModalNewTitle)}
    >
      <Box sx={{ pt: 2 }}>
        <Typography variant="body2">
          <FormattedMessage {...messages.userRfidModalAssigneeText} />
        </Typography>
      </Box>
      <Box sx={{ pt: 2 }}>
        <UserInfo user={user} />
      </Box>
      {isMobileScanActive ? (
        <MobileScanQR keepLeadingZeros onClose={onCloseScanCode} onSuccess={onScanCodeSuccess} />
      ) : (
        <FormProvider {...formMethods}>
          <form onSubmit={formMethods.handleSubmit(onSave)}>
            <Box sx={{ py: formBoxPaddingTop }}>
              {showScanCode ? (
                <>
                  <DesktopScanQR keepLeadingZeros onSuccess={onScanCodeSuccess} />
                  <Box>
                    <ScanQRCodeButton onClick={onToggleScanCode} open={showScanCode} />
                  </Box>
                </>
              ) : (
                <>
                  {isSiteDormakabaIntegrationEnabled ? (
                    <RadioGroupCardType
                      dormakabaContent={
                        <Box sx={{ pl: 4, pt: 2 }}>
                          <CardReader
                            {...DORMAKABA_CARD_READER_PROPS}
                            showCardReaderButton={false}
                            stateOnlyHelperText
                          />
                          <ScanQRCodeButton onClick={onToggleScanCode} open={showScanCode} />
                        </Box>
                      }
                      standardContent={
                        <Box sx={{ pl: 4, pt: 2 }}>
                          <CardReader {...STANDARD_CARD_READER_PROPS} stateOnlyHelperText />
                        </Box>
                      }
                    />
                  ) : (
                    <Box sx={{ pt: 3 }}>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        <FormattedMessage {...messages.userRfidModalCardNormalHelper} />
                      </Typography>
                      <CardReader {...STANDARD_CARD_READER_PROPS} stateOnlyHelperText />
                    </Box>
                  )}
                </>
              )}
            </Box>
            {formMethods.formState.errors?.card?.type === 'isAssigned' && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="error">
                  <FormattedMessage
                    values={{
                      br: <br />,
                      link: (
                        <Link
                          onClick={onClose}
                          to={replaceURLParams({
                            params: { companyId, siteId, userId: assignedRef.current },
                            url: PATHS.SITE_USER_ACCESS,
                          })}
                        >
                          <FormattedMessage {...messages.userRfidModalAlertCardAssignedUserLink} />
                        </Link>
                      ),
                    }}
                    {...messages.userRfidModalAlertCardAssignedText}
                  />
                </Alert>
              </Box>
            )}
          </form>
        </FormProvider>
      )}
    </ActionModal>
  );
}

export default UserRfidModalNew;
